import { safeJsonParse } from '@/components/thread/utils';
import {
  streamServerSentEvents,
  isSseHttpError,
  SseError,
} from '@/lib/deep-mining-sse/fetch-sse2';
import { authTokenManager } from '@/lib/deep-mining/auth-token-manager';
import { useEffect, useRef } from 'react';
import { useStores } from '@/store/hooks/useStore';
import { getRuntimeConfig } from '@/lib/runtime-config';

const API_URL = getRuntimeConfig().BACKEND_URL || '';
// const API_URL = 'http://localhost:6897';


/**
 * 第1次：0.4秒
 * 第2次：0.6秒
 * 第3次：0.9秒
 * 第4次：1.4秒
 * 第5次：2秒
 * 第6次：3秒
 * 第7次：5秒
 * 第8次：7秒
 * 第9次：10秒
 * 第10次：16秒
 * 第11次：23秒
 * 第12次：35秒
 * 第13次：52秒
 * 第14次：79秒（约1.3分钟）
 * 第15次：118秒（约2分钟）
 * 第16次：177秒（约3分钟）
 * 第17次：266秒（约4.4分钟）
 * 第18次：399秒（约6.6分钟）
 * 第19次：598秒（约10分钟）
 * 第20次：898秒（约15分钟）
 */
const CONNECTION_TIMEOUT_MS = 15000; // 连接超时时间（毫秒），后端是10s一次ping
const HTTP_UNAUTHORIZED = 401; // HTTP 未授权状态码
const MAX_RETRY_COUNT = 20; // 最大重试次数
const BASE_RETRY_DELAY_MS = 270; // 基础重试延迟时间（毫秒）
const SCALE = 1.5; // 指数退避倍率

/**
 * 检查是否为 AbortError（直接或包装在 SseError.cause 中）
 */
const isAbortError = (error: any): boolean => {
  try {
    return (
      error?.name === 'AbortError' ||
      (error instanceof SseError &&
        error.cause instanceof Error &&
        error.cause.name === 'AbortError')
    );
  } catch (error) {
    return false;
  }
};

const useApiNotify = () => {
  const stores = useStores();
  const lastTimestampRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initRef = useRef(false);
  const isDestoried = useRef(false);
  const isOnlineRef = useRef(true);
  const resolveRetryRef = useRef<((value?: unknown) => void) | null>(null);
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const statusRef = useRef<'disconnect' | 'wating' | 'connected' | 'error'>(
    'disconnect',
  );

  const clearTimeoutTimer = () => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  };

  const resetTimeoutTimer = () => {
    clearTimeoutTimer();
    timeoutTimerRef.current = setTimeout(() => {
      if (
        statusRef.current === 'error' ||
        statusRef.current === 'disconnect' ||
        isDestoried.current
      )
        return;
      handleError(new DOMException('连接超时', 'TimeoutError'));
    }, CONNECTION_TIMEOUT_MS);
  };

  const handleError = async (error?: any) => {
    // 对于取消请求类型的错误，直接返回，取消请求认为代码中手动触发的，这里防止递归
    if (isAbortError(error)) {
      return;
    }

    clearTimeoutTimer();
    try {
      abortControllerRef.current?.abort();
    } catch (error) {}

    if (error?.name === 'XmUserAuthError') {
      const success = await authTokenManager.refreshAccessToken();
      if (!success) {
        // NOTE: Token刷新失败之后后面的逻辑我这里暂时还是继续走重连
        console.error('[useApiNotify] Token 刷新失败');
      }
    } else {
      // 对于任意非XmUserAuthError类型的错误，这里再静默尝试检查一次token是否过期
      // 保险一点，这个操作是安全的
      authTokenManager.tryRefreshTokenIfNeed();
    }
    statusRef.current = 'error';
    if (!resolveRetryRef.current) {
      handleRetry();
    } else {
      resolveRetryRef.current(null);
    }
    console.error('[useApiNotify] 捕获到错误:', error?.name, error?.message);
    return;
  };

  const handleConnect = async () => {
    abortControllerRef.current = new AbortController();

    try {
      const accessToken = authTokenManager.getAccessToken();
      statusRef.current = 'wating';
      resetTimeoutTimer();

      const params = new URLSearchParams();
      if (typeof lastTimestampRef.current === 'string') {
        params.append('timestamp', lastTimestampRef.current);
      }
      const targetUrl = params.toString()
        ? `${API_URL}/event?${params.toString()}`
        : `${API_URL}/event`;
      for await (const message of streamServerSentEvents(targetUrl, {
        method: 'GET',
        signal: abortControllerRef.current.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })) {
        const parsed = safeJsonParse(message, {} as Record<string, any>);

        // 后端返回error事件，此时前端主动断开并重连
        if (parsed?.event === 'error') {
          const msg = String(message);
          return await handleError(new DOMException(msg, 'XmSseErrorEvent'));
        }

        // 带过去的token过期，需要刷新
        if (
          parsed.event === HTTP_UNAUTHORIZED ||
          parsed.status === HTTP_UNAUTHORIZED
        ) {
          return await handleError(
            new DOMException('Token已过期，需要刷新', 'XmUserAuthError'),
          );
        }

        statusRef.current = 'connected';
        resolveRetryRef.current?.(null);
        resetTimeoutTimer();

        // 与后端约定 前端存储最后一条收到的消息的timestamp，重连的时候带过去，防止漏消息
        if (typeof parsed?.timestamp === 'string') {
          lastTimestampRef.current = parsed.timestamp;
        }

        if (parsed.event && parsed.event !== 'ping') {
          window.dispatchEvent(
            new CustomEvent('api-notify', {
              detail: parsed,
            }),
          );
        }
      }
      // 注意：即使连接Event的时候直接抛出了Http的错误也是走到这里，但是无论如何走到了这里
      // 意味着event接口断开了，前端都得走重连的逻辑
      handleError(new DOMException('异常的断开连接', 'unknownNetworkError'));
    } catch (error: any) {
      // 判断是否为 401 HTTP 错误，需要刷新 token
      if (isSseHttpError(error) && error.status === HTTP_UNAUTHORIZED) {
        return await handleError(
          new DOMException('Token已过期，需要刷新', 'XmUserAuthError'),
        );
      }

      // 其他错误
      return await handleError(error);
    }
  };

  const handleRetry = async () => {
    for (let retryCount = 0; retryCount < MAX_RETRY_COUNT; retryCount++) {
      if (isDestoried.current || !isOnlineRef.current) {
        console.log('[useApiNotify] 停止重试');
        return;
      }

      if (retryCount > 0) {
        const delay = BASE_RETRY_DELAY_MS * Math.pow(SCALE, retryCount);
        console.log(
          `[useApiNotify] 第 ${retryCount} 次重试，延迟 ${Math.round(delay / 1000)}秒`,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      if (isDestoried.current || !isOnlineRef.current) {
        console.log('[useApiNotify] 停止重试');
        return;
      }
      const handleConnectWait = new Promise((resolve) => {
        resolveRetryRef.current = () => {
          resolve(null);
          resolveRetryRef.current = null;
        };
      });
      handleConnect();
      await handleConnectWait;

      if (statusRef.current === 'connected') {
        console.log('[useApiNotify] 连接成功');
        return;
      } else {
        console.log(
          '[useApiNotify] 未连接，继续重试, statusRef.current: ',
          statusRef.current,
        );
      }
    }

    console.log('[useApiNotify] 达到最大重试次数，停止重试');
    statusRef.current = 'error';
  };

  useEffect(() => {
    if (!stores.userStore.userInfo?.user_id || !authTokenManager.isTokenExist) {
      return;
    }
    initRef.current = true;
    isDestoried.current = false;
    isOnlineRef.current = navigator.onLine;
    handleConnect();

    const handleOnline = () => {
      console.log('[useApiNotify] 网络已连接');
      isOnlineRef.current = true;
      if (statusRef.current === 'error' || statusRef.current === 'disconnect') {
        console.log('[useApiNotify] 网络恢复，尝试重新连接');
        handleRetry();
      }
    };

    const handleOffline = () => {
      console.log('[useApiNotify] 网络已断开');
      isOnlineRef.current = false;
      clearTimeoutTimer();
      try {
        abortControllerRef.current?.abort();
      } catch (error) {}
      statusRef.current = 'disconnect';
    };

    // // Debug: 挂载取消方法到 window 对象
    // const cancelApiNotify = () => {
    //   console.log('[useApiNotify] 手动取消连接 (Debug)');
    //   clearTimeoutTimer();
    //   try {
    //     abortControllerRef.current?.abort('DEBUG_CANCEL');
    //   } catch (error) {
    //     console.error('[useApiNotify] 取消失败:', error);
    //   }
    //   statusRef.current = 'disconnect';
    // };
    // (window as any).cancelApiNotify = cancelApiNotify;

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      console.log('[useApiNotify] Hook 销毁');
      isDestoried.current = true;
      clearTimeoutTimer();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      // 移除 debug 方法
      delete (window as any).cancelApiNotify;
      try {
        abortControllerRef.current?.abort('DM_IGNORE_COLLECT');
      } catch (error) {}
    };
  }, [stores.userStore.userInfo?.user_id]);
};

export default useApiNotify;
